// src/rms/pages/Dashboard.js
// ============================================================================
// Operator dashboard
// ============================================================================
// A low-urgency screen, so it carries a sidebar ad slot. Everything it shows is
// derived from the resolved vertical config — a storage operator sees occupied
// units and rent due, a car operator sees vehicles out and returns expected.
// ============================================================================

import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useRms } from "../context/RmsContext";
import { useCollection, indexById } from "../hooks/useCollection";
import db from "../data";
import { PageHeader, Stat, StatusPill, EmptyState, Banner, formatDate, formatDateTime, assetLabel, DemoPill } from "../components/ui";
import SetupProgress from "../components/SetupProgress";
import pinService from "../data/pinService";
import PinModal from "../components/PinModal";

/**
 * Notices from ReserveZoo.
 *
 * RLS does the audience filtering — an operator only ever reads announcements
 * aimed at their trade and plan — so this renders whatever comes back without
 * re-deciding who should see what. Two places deciding the same thing is how
 * they end up disagreeing.
 */
function Announcements() {
  const [rows, setRows] = React.useState([]);

  // Deliberately NOT useCollection: that hook scopes every query to
  // operator_id, and announcements are platform-owned with no such column — so
  // it would silently match nothing, which is exactly what it did.
  React.useEffect(() => {
    let live = true;
    db.list("announcements", {}, { orderBy: "published_at", direction: "desc" }).then((result) => {
      if (live) setRows(result.data || []);
    });
    return () => { live = false; };
  }, []);

  const live = rows.filter((a) => a.published_at && (!a.expires_at || a.expires_at > new Date().toISOString()));
  if (!live.length) return null;

  const tone = { info: "info", warning: "warn", critical: "danger" };

  return (
    <>
      {live.slice(0, 3).map((a) => (
        <Banner key={a.id} tone={tone[a.severity] || "info"}>
          <div>
            <strong>{a.title}</strong>
            <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{a.body}</div>
          </div>
        </Banner>
      ))}
    </>
  );
}

function Dashboard() {
  // No formatMoney: the dashboard shows no money at all. If that changes, it
  // is a decision to reopen rather than an import to add back.
  const { t, tLower, verticalLabel, isContinuous, operator, member, can, revision, refresh } = useRms();

  const { rows: taxRates } = useCollection("taxRates");
  const { rows: members } = useCollection("members");
  const { rows: rateRules } = useCollection("rateRules");
  const { rows: addons } = useCollection("addons");
  const { rows: assets } = useCollection("assets");
  const { rows: reservations } = useCollection("reservations", {}, { orderBy: "starts_at" });
  const { rows: customers } = useCollection("customers");
  // No money is read here at all. The dashboard is the operational board -- what
  // is out, what is coming back, what needs attention -- and money belongs on
  // Billing, behind the roles that govern it.
  //
  // Subscriptions are still loaded, for the past-due BANNER only. That banner
  // names no amount: it says how many accounts are behind and points at the
  // occupancy board, which is a job to do rather than a figure to read. It is
  // skipped for anybody without money rights so the ledger is not transferred
  // to a browser that may not see it.
  const { rows: subscriptions } = useCollection("subscriptions", {}, { skip: !can.viewRevenue });
  const { rows: agreements } = useCollection("agreements");

  // ---- setup checklist ----------------------------------------------------
  // Every item is decided by the data, never by a box someone ticked. An
  // operator who deletes their only asset should see that item come back.
  // Memoised: `|| []` builds a new array every render, which would make the
  // checklist useMemo below recompute on every single render.
  const dismissed = useMemo(
    () => operator?.config_overrides?.setupDismissed || [],
    [operator]
  );

  // Demo rows do not count. seedOperator stamps is_demo on everything it
  // writes, so the bar measures the operator's own setup -- otherwise a new
  // account opens at 7 of 9, congratulating them for sample data they never
  // touched. Read from the row, not from a list of ids on the operator: that
  // list lived in the same jsonb as setupDismissed and setupCollapsed and was
  // silently dropped by any write that spread a stale config_overrides.
  const mine = useMemo(() => {
    const own = (rows) => rows.filter((r) => !r.is_demo);
    return {
      assets: own(assets),
      customers: own(customers),
      reservations: own(reservations),
      rateRules: own(rateRules),
      addons: own(addons),
    };
  }, [assets, customers, reservations, rateRules, addons]);

  // Declared above setupItems because that useMemo reads it. Defaults to true
  // so the checklist does not flash "set your PIN" at somebody who already has
  // one while the answer is still in flight.
  const [hasOwnPin, setHasOwnPin] = useState(true);
  const [settingPin, setSettingPin] = useState(false);
  useEffect(() => {
    pinService.hasPin(operator, member).then((r) => setHasOwnPin(!!r.data));
  }, [operator, member, revision]);

  const setupItems = useMemo(() => {
    const defs = [
      {
        key: "tax",
        label: t("setup.tax", "Set up your tax rates"),
        hint: t("setup.taxHint", "Until you do, invoices are raised with no tax on them."),
        to: "/rms/settings/tax",
        done: taxRates.some((r) => r.is_active !== false),
      },
      {
        key: "inventory",
        label: t("setup.inventory", "Add something to rent out"),
        to: "/rms/inventory",
        done: mine.assets.length > 0,
      },
      {
        key: "rates",
        label: t("setup.rates", "Give it a base rate"),
        hint: t("setup.ratesHint", "A booking cannot be priced without one."),
        to: "/rms/inventory",
        done: mine.assets.some((a) => Number(a.base_rate_cents) > 0),
      },
      {
        key: "country",
        label: t("setup.country", "Confirm your country"),
        hint: t("setup.countryHint", "Used for currency, dates and, later, tax reporting."),
        to: "/rms/settings",
        done: !!operator?.country,
      },
      {
        key: "customer",
        label: t("setup.customer", "Add someone to rent to"),
        to: "/rms/customers",
        done: mine.customers.length > 0,
      },
      {
        key: "booking",
        label: t("setup.booking", "Take a first booking"),
        to: "/rms/reservations",
        done: mine.reservations.length > 0,
      },
      {
        // Only once there is somebody to be told apart from. A solo operator
        // has nothing to attribute, and asking them to invent a PIN on day one
        // is a step that protects nothing.
        key: "pin",
        label: t("setup.pin", "Set your staff PIN"),
        hint: t("setup.pinHint", "Four digits that sign what you do, so the log shows who did it."),
        // Opens here rather than linking to Settings, which is behind a PIN
        // gate an owner-only one at that. Somebody with no PIN was being sent
        // to a door only the thing they lacked could open.
        onAction: () => setSettingPin(true),
        done: hasOwnPin,
        // Shown to everyone now, not only once a second member exists. A sole
        // owner signs their own checkouts, and under the current design a
        // member without a PIN cannot complete one.
      },
      {
        key: "staff",
        label: t("setup.staff", "Invite your team"),
        to: "/rms/settings/staff",
        // Was members.length > 1, which needed TWO invites before it ticked --
      // the owner had no member row of their own, so the count started at
      // zero. Now that provisioning always seats the owner, the question is
      // "is there anybody here besides me".
      done: members.some((m) => m.role !== "owner"),
      },
      {
        key: "pricing",
        label: t("setup.pricing", "Add a rate rule"),
        hint: t("setup.pricingHint", "Seasonal pricing, length-of-hire discounts, that sort of thing."),
        to: "/rms/pricing",
        done: mine.rateRules.length > 0,
      },
      {
        key: "addons",
        label: t("setup.addons", "Add your extras"),
        to: "/rms/pricing/addons",
        done: mine.addons.length > 0,
      },
    ];
    // A dismissed item that has since been done is no longer dismissed --
    // the data outranks the preference.
    return defs
      .filter((d) => !d.hidden)
      .map((d) => ({ ...d, dismissed: !d.done && dismissed.includes(d.key) }));
  }, [t, taxRates, mine, operator, members, dismissed, hasOwnPin]);

  const setDismissed = async (keys) => {
    if (!operator) return;
    await db.update("operators", operator.id, {
      config_overrides: { ...(operator.config_overrides || {}), setupDismissed: keys },
    });
    await refresh();
  };
  // Collapse state rides on the same jsonb as the dismissals, so it follows the
  // operator between browsers rather than being a per-device quirk.
  const setupCollapsed = !!operator?.config_overrides?.setupCollapsed;
  const toggleSetup = async () => {
    if (!operator) return;
    await db.update("operators", operator.id, {
      config_overrides: { ...(operator.config_overrides || {}), setupCollapsed: !setupCollapsed },
    });
    await refresh();
  };

  const dismissSetup = (key) => setDismissed([...new Set([...dismissed, key])]);
  const restoreSetup = (key) => setDismissed(dismissed.filter((k) => k !== key));

  const assetsById = useMemo(() => indexById(assets), [assets]);
  const customersById = useMemo(() => indexById(customers), [customers]);

  const outNow = useMemo(
    () => reservations.filter((r) => ["checked_out", "active"].includes(r.status)),
    [reservations]
  );

  const upcoming = useMemo(() => {
    const now = Date.now();
    return reservations
      .filter((r) => r.status === "confirmed" && new Date(r.starts_at).getTime() >= now)
      .slice(0, 6);
  }, [reservations]);

  const overdue = useMemo(() => {
    const now = Date.now();
    return reservations.filter(
      (r) => r.status === "checked_out" && r.ends_at && new Date(r.ends_at).getTime() < now
    );
  }, [reservations]);

  /**
   * Coming back before the day is out.
   *
   * The number a counter actually works from: how many bays will be needed,
   * how many walk-ins can be promised something. `overdue` below is the same
   * question asked too late.
   */
  const returningToday = useMemo(() => {
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);
    const cutoff = endOfDay.getTime();
    const now = Date.now();
    return reservations.filter(
      (r) =>
        r.status === "checked_out" &&
        r.ends_at &&
        new Date(r.ends_at).getTime() <= cutoff &&
        new Date(r.ends_at).getTime() >= now
    );
  }, [reservations]);

  /** Continuous trades: leases whose term ends within the month. */
  const movingOut = useMemo(() => {
    const cutoff = Date.now() + 30 * 864e5;
    return (subscriptions || []).filter(
      (sub) => sub.status === "active" && sub.ends_at && new Date(sub.ends_at).getTime() <= cutoff
    );
  }, [subscriptions]);

  const availableCount = assets.filter((a) => a.status === "available").length;
  const serviceCount = assets.filter((a) => ["maintenance", "out_of_service"].includes(a.status)).length;

  // Outstanding and monthly-recurring totals used to be summed here from every
  // invoice in the business, then briefly hidden behind a PIN. Both are gone:
  // this screen carries no money at all now. rms_revenue_summary still exists
  // and is still the right way to read totals with a PIN, for whatever screen
  // wants them next -- it is Billing's to use, not the dashboard's.

  const pastDueSubs = subscriptions.filter((s) => s.status === "past_due");

  const openAgreements = agreements.filter((a) => a.status === "open");

  return (
    <>
      <PageHeader
        title={t("dashboard.dashboard", "Dashboard")}
        subtitle={t("dashboard.subtitle", "{trade} · {count} {items} on the books", {
          trade: verticalLabel,
          count: assets.length,
          items: tLower("assetPlural", "assets"),
        })}
        actions={
          <>
            <Link className="rms-btn rms-btn--ghost" to="/rms/inventory">
              {t("dashboard.manageFleet", "Manage {fleet}", { fleet: tLower("fleet", "inventory") })}
            </Link>
            <Link className="rms-btn rms-btn--primary" to="/rms/reservations?new=1">
              {t("dashboard.newBooking", "New {booking}", { booking: tLower("reservation", "reservation") })}
            </Link>
          </>
        }
      />

      {settingPin ? (
        <PinModal
          forceSetup
          onCancel={() => setSettingPin(false)}
          onResolved={async () => {
            setSettingPin(false);
            setHasOwnPin(true);
            return {};
          }}
        />
      ) : null}

      <SetupProgress
        items={setupItems}
        onDismiss={dismissSetup}
        onRestore={restoreSetup}
        collapsed={setupCollapsed}
        onToggle={toggleSetup}
      />

      <Announcements />

      {overdue.length > 0 ? (
        <Banner tone="danger">
          <strong>{overdue.length} overdue.</strong> These were due back and haven't been checked in
          yet — {overdue.map((r) => assetLabel(assetsById[r.asset_id], "")).filter(Boolean).join(", ")}.
        </Banner>
      ) : null}

      {pastDueSubs.length > 0 ? (
        <Banner tone="warn">
          <strong>
            {t("dashboard.pastDue", "{count, plural, one {# past-due account.} other {# past-due accounts.}}", {
              count: pastDueSubs.length,
            })}
          </strong>{" "}
          {t("dashboard.pastDueBody", "Rent is unpaid past the grace period. Check the Occupancy board for lien status.")}
        </Banner>
      ) : null}

  <div className="rms-stat-row">
        {isContinuous ? (
          <>
            <Stat label={t("dashboard.unitsOccupied", "Units occupied")} value={assets.filter((a) => a.status === "occupied").length} hint={`${availableCount} vacant`} />
            <Stat
              label={t("dashboard.vacant", "Vacant")}
              value={availableCount}
              hint={t("dashboard.readyToLet", "ready to let")}
            />
            <Stat
              label={t("dashboard.movingOut", "Moving out")}
              value={movingOut.length}
              hint={t("dashboard.within30Days", "within 30 days")}
            />
            <Stat label={t("dashboard.occupancyRate", "Occupancy rate")} value={assets.length ? `${Math.round((assets.filter((a) => a.status === "occupied").length / assets.length) * 100)}%` : "—"} />
              </>
            ) : (
              <>
                <Stat label={t("dashboard.assetsOut", "{items} out", { items: t("assetPlural", "Assets") })} value={outNow.length} hint={t("dashboard.availableNow", "{count} available now", { count: availableCount })} />
                <Stat
                  label={t("dashboard.upcoming", "Upcoming")}
                  value={upcoming.length}
                  hint={t("dashboard.upcomingHint", "confirmed, not yet out")}
                />
                <Stat
                  label={t("dashboard.returning", "Returning today")}
                  value={returningToday.length}
                  hint={t("dashboard.dueBackToday", "due back before tonight")}
                />
                <Stat label={t("dashboard.inService", "In service")} value={serviceCount} hint={t("dashboard.inServiceHint", "maintenance or cleaning")} />
              </>
            )}
          </div>

          <div className="rms-card" style={{ marginBottom: 20 }}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--rms-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 className="rms-section-title" style={{ margin: 0 }}>
                {isContinuous ? t("dashboard.currentlyOccupied", "Currently occupied") : t("dashboard.outNow", "Out now")}
              </h2>
              <Link to="/rms/reservations" style={{ fontSize: 12.5, color: "var(--rms-brand)", fontWeight: 620 }}>
                {t("dashboard.viewAll", "View all")} →
              </Link>
            </div>

            {outNow.length === 0 ? (
              <div style={{ padding: 28, textAlign: "center", color: "var(--rms-muted-strong)", fontSize: 13.5 }}>
                {t("dashboard.nothingOut", "Nothing is out right now.")}
              </div>
            ) : (
              <div className="rms-table-wrap" style={{ border: "none", borderRadius: 0 }}>
                <table className="rms-table">
                  <thead>
                    <tr>
                      <th>{t("assetSingular", "Asset")}</th>
                      <th>{t("customer", "Customer")}</th>
                      <th>{isContinuous ? t("dashboard.since", "Since") : t("reservations.dueBack", "Due back")}</th>
                      <th>{t("inventory.status", "Status")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {outNow.map((r) => {
                      const asset = assetsById[r.asset_id];
                      const customer = customersById[r.customer_id];
                      const agreement = openAgreements.find((a) => a.reservation_id === r.id);
                      const isLate = r.ends_at && new Date(r.ends_at).getTime() < Date.now();
                      return (
                        <tr key={r.id}>
                          <td>
                            <strong>{asset?.name || "—"}</strong>
                            {r.is_demo ? <> <DemoPill /></> : null}
                            <div style={{ fontSize: 12, color: "var(--rms-muted)" }}>{asset?.asset_code}</div>
                          </td>
                          <td>{customer ? `${customer.first_name} ${customer.last_name || ""}`.trim() : "—"}</td>
                          <td style={{ color: isLate ? "var(--rms-danger)" : undefined, fontWeight: isLate ? 650 : undefined }}>
                            {isContinuous ? formatDate(r.starts_at) : formatDateTime(r.ends_at)}
                          </td>
                          <td>
                            <StatusPill status={isLate ? "overdue" : r.status} />
                          </td>
                          <td className="rms-num">
                            {agreement ? (
                              <Link className="rms-btn rms-btn--ghost rms-btn--sm" to={`/rms/checkin/${agreement.id}`}>
                                {t("checkin", "Check in")}
                              </Link>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {!isContinuous ? (
            <div className="rms-card">
              <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--rms-border)" }}>
                <h2 className="rms-section-title" style={{ margin: 0 }}>
                  {t("dashboard.comingUp", "Coming up")}
                </h2>
              </div>
              {upcoming.length === 0 ? (
                <div style={{ padding: 28, textAlign: "center", color: "var(--rms-muted-strong)", fontSize: 13.5 }}>
                  {t("dashboard.noUpcoming", "No upcoming {bookings}.", {
              bookings: tLower("reservationPlural", "reservations"),
            })}
                </div>
              ) : (
                <div className="rms-table-wrap" style={{ border: "none", borderRadius: 0 }}>
                  <table className="rms-table">
                    <thead>
                      <tr>
                        <th>{t("assetSingular", "Asset")}</th>
                        <th>{t("customer", "Customer")}</th>
                        <th>{t("campaignForm.starts", "Starts")}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {upcoming.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <strong>{assetsById[r.asset_id]?.name || "—"}</strong>
                            {r.is_demo ? <> <DemoPill /></> : null}
                          </td>
                          <td>
                            {customersById[r.customer_id]
                              ? `${customersById[r.customer_id].first_name} ${customersById[r.customer_id].last_name || ""}`.trim()
                              : "—"}
                          </td>
                          <td>{formatDateTime(r.starts_at)}</td>
                          <td className="rms-num">
                            <Link className="rms-btn rms-btn--primary rms-btn--sm" to={`/rms/checkout/${r.id}`}>
                              {t("checkout", "Check out")}
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}

          {assets.length === 0 ? (
            <EmptyState
              title={t("dashboard.empty.title", "Add your first {item}", {
            item: tLower("assetSingular", "asset"),
          })}
              message={t("dashboard.empty.body", "Your {fleet} is empty. Add something to rent out and you can start taking bookings.", {
            fleet: tLower("fleet", "inventory"),
          })}
              action={
                <Link className="rms-btn rms-btn--primary" to="/rms/inventory?new=1">
                  {t("dashboard.addAsset", "Add {item}", { item: tLower("assetSingular", "asset") })}
                </Link>
              }
            />
          ) : null}
    </>
  );
}

export default Dashboard;
